import { useEffect, useRef, type CSSProperties } from "react";
import gsap from "gsap";

type PointerAuraProps = {
  color: string;
  className?: string;
  label?: string;
};

export function PointerAura({ color, className = "", label }: PointerAuraProps) {
  const auraRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const aura = auraRef.current;
    if (
      !aura ||
      window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    gsap.set(aura, { xPercent: -50, yPercent: -50 });
    const moveX = gsap.quickTo(aura, "x", { duration: 0.42, ease: "power3.out" });
    const moveY = gsap.quickTo(aura, "y", { duration: 0.42, ease: "power3.out" });

    const onMove = (event: PointerEvent) => {
      moveX(event.clientX);
      moveY(event.clientY);
      aura.dataset.visible = "true";
    };
    const onLeave = () => {
      aura.dataset.visible = "false";
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);

    return () => {
      window.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div
      ref={auraRef}
      className={`pointer-aura ${className}`.trim()}
      style={{ "--aura-color": color } as CSSProperties}
      aria-hidden="true"
    >
      {label && <span>{label}</span>}
    </div>
  );
}
